import { create } from 'zustand'
import type { Organization } from '@/services/api/organizations'
import type { Deployment } from '@/services/api/deployments'
import {
  preferSessionName,
  type Space,
  type SpaceListedSession,
} from '@/services/api/spaces'

export type PostureType = 'build' | 'operate' | 'calisto'

function mergeSpaceSession(
  current: SpaceListedSession,
  next: SpaceListedSession,
): SpaceListedSession {
  return {
    ...current,
    ...next,
    name:
      preferSessionName(next.name, current.name) ||
      next.name ||
      current.name ||
      '',
  }
}

/** Merge hydrated spaces so a later profile refresh restores session names. */
export function mergeHydratedSpaces(current: Space[], next: Space[]): Space[] {
  if (current.length === 0) {
    return next
  }

  const currentById = new Map(current.map((space) => [space.id, space]))

  return next.map((space) => {
    const previous = currentById.get(space.id)
    if (!previous) {
      return space
    }

    const previousSessions = previous.sessions || []
    const nextSessions = space.sessions || []
    const previousById = new Map(
      previousSessions.map((session) => [session.id, session]),
    )
    const mergedFromNext = nextSessions.map((session) => {
      const existing = previousById.get(session.id)
      return existing ? mergeSpaceSession(existing, session) : session
    })
    const nextIds = new Set(nextSessions.map((session) => session.id))
    const retained = previousSessions.filter((session) => !nextIds.has(session.id))

    return {
      ...previous,
      ...space,
      sessions: [...mergedFromNext, ...retained],
      sessionCount: Math.max(
        space.sessionCount ?? 0,
        previous.sessionCount ?? 0,
        mergedFromNext.length + retained.length,
      ),
    }
  })
}

interface ContextStore {
  organizations: Organization[]
  deployments: Deployment[]
  spaces: Space[]
  org: string | null
  deploymentId: string | null
  env: string | null
  workspace: string | null
  posture: PostureType
  isLoading: boolean
  error: string | null
  hydrate: (state: Partial<ContextStore>) => void
  setOrganizations: (organizations: Organization[]) => void
  setDeployments: (deployments: Deployment[]) => void
  setSpaces: (spaces: Space[]) => void
  upsertSpace: (space: Space) => void
  removeSpace: (spaceId: string) => void
  upsertSpaceSession: (spaceId: string, session: SpaceListedSession) => void
  removeSpaceSession: (spaceId: string, sessionId: string) => void
  setOrg: (org: string | null) => void
  setDeploymentId: (deploymentId: string | null) => void
  setEnv: (env: string | null) => void
  setWorkspace: (workspace: string | null) => void
  setPosture: (posture: PostureType) => void
  setLoading: (isLoading: boolean) => void
  setError: (error: string | null) => void
  resetContext: () => void
}

export const useContextStore = create<ContextStore>((set) => ({
  organizations: [],
  deployments: [],
  spaces: [],
  org: null,
  deploymentId: null,
  env: null,
  workspace: null,
  posture: 'operate',
  isLoading: false,
  error: null,
  hydrate: (state) => set((current) => ({ ...current, ...state })),
  setOrganizations: (organizations) => set({ organizations }),
  setDeployments: (deployments) => set({ deployments }),
  setSpaces: (spaces) => set({ spaces }),
  upsertSpace: (space) =>
    set((state) => {
      const exists = state.spaces.some((item) => item.id === space.id)
      return {
        spaces: exists
          ? state.spaces.map((item) =>
              item.id === space.id ? { ...item, ...space } : item,
            )
          : [space, ...state.spaces],
      }
    }),
  removeSpace: (spaceId) =>
    set((state) => ({
      spaces: state.spaces.filter((space) => space.id !== spaceId),
      workspace: state.workspace === spaceId ? state.spaces.find((space) => space.id !== spaceId)?.id ?? null : state.workspace,
    })),
  upsertSpaceSession: (spaceId, session) =>
    set((state) => ({
      spaces: state.spaces.map((space) => {
        if (space.id !== spaceId) {
          return space
        }

        const currentSessions = space.sessions || []
        const sessionExists = currentSessions.some((item) => item.id === session.id)
        const sessions = sessionExists
          ? currentSessions.map((item) =>
              item.id === session.id ? mergeSpaceSession(item, session) : item,
            )
          : [session, ...currentSessions]

        return {
          ...space,
          sessions,
          sessionCount: Math.max(space.sessionCount ?? 0, sessions.length),
          updatedAt: session.updatedAt || space.updatedAt,
        }
      }),
    })),
  removeSpaceSession: (spaceId, sessionId) =>
    set((state) => ({
      spaces: state.spaces.map((space) => {
        if (space.id !== spaceId) {
          return space
        }

        const sessions = (space.sessions || []).filter((session) => session.id !== sessionId)

        return {
          ...space,
          sessions,
          sessionCount:
            typeof space.sessionCount === 'number'
              ? Math.max(0, space.sessionCount - 1)
              : sessions.length,
        }
      }),
    })),
  setOrg: (org) => set({ org }),
  setDeploymentId: (deploymentId) =>
    set((state) => {
      const deployment = state.deployments.find((item) => item.id === deploymentId)
      return {
        deploymentId,
        env: deployment?.environment || null,
      }
    }),
  setEnv: (env) => set({ env }),
  setWorkspace: (workspace) => set({ workspace }),
  setPosture: (posture) => set({ posture }),
  setLoading: (isLoading) => set({ isLoading }),
  setError: (error) => set({ error }),
  resetContext: () =>
    set({
      organizations: [],
      deployments: [],
      spaces: [],
      org: null,
      deploymentId: null,
      env: null,
      workspace: null,
      posture: 'operate',
      isLoading: false,
      error: null,
    }),
}))
